import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container, Section } from '../../styles/GlobalStyles';

const BenefitsContainer = styled(Section)`
  background: ${theme.colors.neutral.white};
  padding: ${theme.spacing['4xl']} 0;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.xl} 0;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg} 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};

  @media (max-width: ${theme.breakpoints.md}) {
    margin-bottom: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.lg};
  }
`;

const SectionTitle = styled.h2`
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: ${theme.spacing.sm};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.neutral.gray600};
  max-width: 700px;
  margin: 0 auto;
  line-height: ${theme.typography.lineHeight.relaxed};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['2xl']};
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.md};
  }
`;

const BenefitItem = styled.div<{ color: string }>`
  display: flex;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.neutral.light};
  /* Borda sempre presente (transparente) para que o hover não reflua o layout. */
  border: 2px solid transparent;
  transition: all ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.neutral.white};
    border-color: ${props => props.color};
    transform: translateY(-4px);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: row;
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.md};
  }
`;

const BenefitIconWrapper = styled.div<{ color: string }>`
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.neutral.white};

  svg {
    width: 28px;
    height: 28px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 48px;
    height: 48px;
    min-width: 48px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const BenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const BenefitTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.primary.data};
  margin-bottom: ${theme.spacing.xs};
  line-height: ${theme.typography.lineHeight.tight};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
  }
`;

const BenefitDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral.gray600};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.sm};
  }
`;

export interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface BenefitsSectionProps {
  title: string;
  subtitle: string;
  benefits: Benefit[];
  color: string;
}

const BenefitsSection: React.FC<BenefitsSectionProps> = ({
  title,
  subtitle,
  benefits,
  color,
}) => {
  return (
    <BenefitsContainer>
      <Container>
        <SectionHeader>
          <SectionTitle>{title}</SectionTitle>
          <SectionSubtitle>{subtitle}</SectionSubtitle>
        </SectionHeader>

        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitItem key={index} color={color}>
              <BenefitIconWrapper color={color}>
                {benefit.icon}
              </BenefitIconWrapper>
              <BenefitContent>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitContent>
            </BenefitItem>
          ))}
        </BenefitsGrid>
      </Container>
    </BenefitsContainer>
  );
};

export default BenefitsSection;




